import {X} from 'lucide-react'
import { OIL_DATABASE, type OilEntry, type Unit } from "@/data/oilDatabase"

interface OilSelectionProps {
  oils: OilEntry[]
  batchSize: number
  unit: Unit
  totalPercentage: number
  addOil: (name: string) => void
  removeOil: (id: string) => void
  updateOil: (id: string, field: keyof OilEntry, value: number | string) => void
  balanceToHundred: () => void
}

export function OilSelection({
  oils,
  batchSize,
  unit,
  totalPercentage,
  addOil,
  removeOil,
  updateOil,
  balanceToHundred,
}: OilSelectionProps) {
  return (
    <section className="lg:col-span-6">
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-900">Oil Selection</h2>
          <div className="flex items-center gap-2">
            <span
              className={`text-sm font-semibold ${
                Math.abs(totalPercentage - 100) < 0.1
                  ? "text-emerald-600"
                  : "text-amber-600"
              }`}
            >
              {totalPercentage.toFixed(1)}%
            </span>
            <button
              onClick={balanceToHundred}
              className="px-3 py-1.5 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white text-sm rounded-lg hover:from-emerald-600 hover:to-emerald-700 transition-all shadow-sm"
            >
              Balance to 100%
            </button>
          </div>
        </div>

        {/* Add Oil Dropdown */}
        <div className="mb-4">
          <select
            onChange={(e) => {
              if (e.target.value) {
                addOil(e.target.value)
                e.target.value = ""
              }
            }}
            className="w-full py-2 px-3 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          >
            <option value="">+ Add Oil</option>
            {OIL_DATABASE.filter(
              (oil) => !oils.some((o) => o.oil === oil.name)
            ).map((oil) => (
              <option key={oil.name} value={oil.name}>
                {oil.name}
              </option>
            ))}
          </select>
        </div>

        {/* Oil Table - Desktop */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left py-3 px-3 text-xs font-semibold text-gray-700">Oil</th>
                <th className="text-center py-3 px-3 text-xs font-semibold text-gray-700">%</th>
                <th className="text-center py-3 px-3 text-xs font-semibold text-gray-700">Weight</th>
                <th className="text-center py-3 px-3 text-xs font-semibold text-gray-700">Cost/Unit</th>
                <th className="w-12"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {oils.map((oil) => {
                const calculatedWeight = (oil.percentage / 100) * batchSize
                return (
                  <tr key={oil.id} className="hover:bg-gray-50">
                    <td className="py-3 px-3">
                      <span className="text-sm font-medium text-gray-900">{oil.oil}</span>
                    </td>
                    <td className="py-3 px-3">
                      <input
                        type="number"
                        value={oil.percentage || ""}
                        onChange={(e) => updateOil(oil.id, "percentage", Number(e.target.value))}
                        className="w-20 py-1.5 px-2 text-center bg-gray-50 border border-gray-300 rounded focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        min="0"
                        max="100"
                        step="0.1"
                      />
                    </td>
                    <td className="py-3 px-3 text-center">
                      <span className="text-sm text-gray-700">
                        {calculatedWeight.toFixed(1)} {unit}
                      </span>
                    </td>
                    <td className="py-3 px-3">
                      <input
                        type="number"
                        value={oil.costPerUnit || ""}
                        onChange={(e) => updateOil(oil.id, "costPerUnit", Number(e.target.value))}
                        className="w-20 py-1.5 px-2 text-center bg-gray-50 border border-gray-300 rounded focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                      />
                    </td>
                    <td className="py-3 px-3 text-center">
                      <button
                        onClick={() => removeOil(oil.id)}
                        className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <X size={18} />
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {/* Oil Cards - Mobile */}
        <div className="md:hidden space-y-3">
          {oils.map((oil) => {
            const calculatedWeight = (oil.percentage / 100) * batchSize
            return (
              <div key={oil.id} className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-semibold text-gray-900">{oil.oil}</h3>
                  <button
                    onClick={() => removeOil(oil.id)}
                    className="p-1 text-red-500 hover:bg-red-100 rounded"
                  >
                    <X size={18} />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Percentage</label>
                    <input
                      type="number"
                      value={oil.percentage || ""}
                      onChange={(e) => updateOil(oil.id, "percentage", Number(e.target.value))}
                      className="w-full py-2 px-3 bg-white border border-gray-300 rounded-lg"
                      min="0"
                      max="100"
                      step="0.1"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Weight</label>
                    <div className="py-2 px-3 bg-white border border-gray-200 rounded-lg">
                      <span className="text-sm text-gray-900">
                        {calculatedWeight.toFixed(1)} {unit}
                      </span>
                    </div>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs text-gray-600 mb-1">Cost per {unit}</label>
                    <input
                      type="number"
                      value={oil.costPerUnit || ""}
                      onChange={(e) => updateOil(oil.id, "costPerUnit", Number(e.target.value))}
                      className="w-full py-2 px-3 bg-white border border-gray-300 rounded-lg"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                    />
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {oils.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <p className="mb-2">No oils added yet</p>
            <p className="text-sm">Select an oil from the dropdown above to get started</p>
          </div>
        )}
      </div>
    </section>
  )
}
